import { useState } from "react";
import { Star, Send, Quote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export const FeedbackSection = ({ embedded = false, defaultName = "", defaultEmail = "" }) => {
  const [form, setForm] = useState({ name: defaultName || "", email: defaultEmail || "", role: "", message: "" });
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/feedback", { ...form, rating: rating || null });
      toast.success("Thank you for sharing your feedback.");
      setForm({ name: defaultName || "", email: defaultEmail || "", role: "", message: "" });
      setRating(0);
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0d0d0d] border border-white/12 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all";

  const card = sent ? (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-10 text-center" data-testid="feedback-thankyou">
      <Star size={28} className="mx-auto text-zinc-300 mb-4" />
      <h3 className="font-dash text-2xl">Thank you</h3>
      <p className="text-zinc-500 mt-3">Your feedback has been received with thanks.</p>
      <button onClick={() => setSent(false)} className="mt-6 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="feedback-again">Submit another response</button>
    </div>
  ) : (
    <form onSubmit={submit} data-testid="feedback-form" className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-8 space-y-4">
      <div>
        <label className="text-xs text-zinc-500 uppercase tracking-wider">How would you rate your experience?</label>
        <div className="flex items-center gap-2 mt-3" data-testid="feedback-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button type="button" key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}
              className="transition-transform hover:scale-110" data-testid={`feedback-star-${n}`}>
              <Star size={28} className={(hover || rating) >= n ? "text-amber-300 fill-amber-300" : "text-zinc-600"} strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required data-testid="feedback-name" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
        <input type="email" data-testid="feedback-email" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
      </div>
      <input data-testid="feedback-role" className={inputCls} placeholder="Your role (e.g. party, solicitor, counsel) — optional" value={form.role} onChange={set("role")} />
      <textarea required rows={5} data-testid="feedback-message" className={inputCls} placeholder="Tell us about your experience of the mediation…" value={form.message} onChange={set("message")} />
      <button type="submit" disabled={loading} data-testid="feedback-submit" className="btn-metal w-full py-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60">
        {loading ? "Sending…" : <>Submit feedback <Send size={15} /></>}
      </button>
      <p className="text-zinc-600 text-xs text-center">Your feedback is confidential and reviewed personally.</p>
    </form>
  );

  if (embedded) return <div>{card}</div>;

  return (
    <section className="border-t border-white/10 bg-[#0a0a0a]">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-28">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <Quote size={20} strokeWidth={1.5} className="text-zinc-500" />
            <div className="overline">Your Experience</div>
          </div>
          <h2 className="font-display font-light text-4xl lg:text-5xl leading-tight">Share your feedback.</h2>
          <p className="text-zinc-400 mt-6 leading-relaxed">
            If you have taken part in a mediation with Tactis, we welcome your reflections. Your feedback is confidential and helps us continually improve the process.
          </p>
        </Reveal>
        <Reveal delay={0.1}><div className="mt-12">{card}</div></Reveal>
      </div>
    </section>
  );
};
